import React, { useEffect, useState } from "react";
import { FaEdit, FaTrashAlt } from "react-icons/fa";

// Componentes comunes
import ListHeader from "../../../components/listing/ListHeader";
import ListFooter from "../../../components/listing/ListFooter";
// IMPORTANTE: Importamos DynamicTable
import DynamicTable from "../../../components/listing/DynamicTable";

// Sub-componentes
import FiltroProdCertificado from "./FiltroProdCertificado";
import CrearProdCertificado from "./CrearProdCertificado";
import EditarProdCertificado from "./EditarProdCertificado";
import EliminarProdCertificado from "./EliminarProdCertificado";

import apiRoutes from "../../../api/apiRoutes";
import { appFetchJson } from "../../../utils/http";

export default function ListarProdCertificado() {
  // ================================
  //   PAGINACIÓN
  // ================================
  const [porPagina, setPorPagina] = useState(30);
  const [pagina, setPagina] = useState(1);
  const [totalBackend, setTotalBackend] = useState(0);

  // ================================
  //   DATA
  // ================================
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Modales
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [mostrarCrear, setMostrarCrear] = useState(false);
  const [registroAEditar, setRegistroAEditar] = useState(null);
  const [registroAEliminar, setRegistroAEliminar] = useState(null);

  // ================================
  //   FILTROS
  // ================================
  const [filtros, setFiltros] = useState({
    certificadoId: "",
    productoId: "",
    valorMontoDescuento: "",
    codigoMes: "",
    qnProducto: "", 
    qnUtilizado: "" 
  });

  const [reloadKey, setReloadKey] = useState(0);

  // ================================
  //   CARGA DE DATOS
  // ================================
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        let baseUrl = apiRoutes.certificadoDetalles.listUrl({});

        if (baseUrl.includes("?")) {
          baseUrl = baseUrl.split("?")[0];
        }

        const queryParams = new URLSearchParams();
        queryParams.append("page", pagina - 1);
        queryParams.append("size", porPagina);
        
        // Mapeo de Filtros
        if (filtros.certificadoId) queryParams.append("certificadoId", filtros.certificadoId);
        if (filtros.productoId) queryParams.append("productoId", filtros.productoId);
        if (filtros.valorMontoDescuento) queryParams.append("valorMontoDescuento", filtros.valorMontoDescuento);
        if (filtros.codigoMes) queryParams.append("codigoMes", filtros.codigoMes);
        if (filtros.qnProducto) queryParams.append("qnProducto", filtros.qnProducto);
        if (filtros.qnUtilizado) queryParams.append("qnUtilizado", filtros.qnUtilizado);

        const url = `${baseUrl}?${queryParams.toString()}`;
        const res = await appFetchJson(url);

        const items = Array.isArray(res?.items) ? res.items : [];
        setData(items);

        const total =
          res?.totalElements ??
          res?.total ??
          (Array.isArray(items) ? items.length : 0);

        setTotalBackend(total);

      } catch (e) {
        console.error("Error cargando productos de certificado:", e);
        setError("Error al cargar los datos.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [pagina, porPagina, filtros, reloadKey]);

  // Helper para formatear números
  const formatNumber = (val) => {
    if (val === null || val === undefined) return "";
    return new Intl.NumberFormat("es-PY").format(val);
  };

  // ====================================================================
  // DEFINICIÓN DE COLUMNAS (TOTAL 100%)
  // ====================================================================
  const columns = [
    // 1. CERTIFICADO (15%)
    { 
      header: "Certificado", 
      accessor: "certificadoNumero",
      type: "text", 
      width: "15%", 
      cellClass: "font-medium text-gray-800" 
    },
    // 2. PRODUCTO (25%)
    { 
      header: "Producto", 
      accessor: "productoNombre",
      type: "text", 
      width: "25%", 
      cellClass: "whitespace-normal break-words leading-tight"
    },
    // 3. CANT. PRODUCTO (15%)
    { 
      header: "Cant. del producto", 
      className: "text-right",
      type: "center", 
      width: "15%", 
      // Forzamos alineación derecha para números
      cellClass: "text-right font-medium text-gray-700",
      render: (row) => formatNumber(row.qnProducto)
    },
    // 4. MONTO DESCUENTO (15%)
    { 
      header: "Monto del Descuento", 
      className: "text-right",
      type: "center", 
      width: "15%", 
      cellClass: "text-right font-medium text-gray-700", 
      render: (row) => formatNumber(row.valorMontoDescuento)
    },
    // 5. UTILIZADO (15%)
    { 
      header: "Utilizado hasta el momento", 
      className: "text-right",
      type: "center", 
      width: "15%", 
      cellClass: "text-right font-medium text-gray-700", 
      render: (row) => formatNumber(row.qnUtilizado)
    },
    // 6. ACCIÓN (15%)
    {
      header: "Acción",
      type: "action", 
      width: "15%",
      render: (it) => (
        <div className="flex justify-center items-center gap-2">
          <button
            className="text-gray-500 hover:text-blue-600 p-1"
            title="Editar"
            onClick={() => setRegistroAEditar(it)}
          >
            <FaEdit />
          </button>
          <button
            className="text-gray-500 hover:text-red-600 p-1"
            title="Eliminar"
            onClick={() => setRegistroAEliminar(it)}
          >
            <FaTrashAlt />
          </button>
        </div>
      )
    }
  ];

  // ================================
  //   RENDER
  // ================================
  return (
    <div className="page">

      <ListHeader
        title="Productos de certificados"
        filters={filtros}
        onFilterClick={() => setMostrarFiltros(true)}
        onClearFilters={() => {
          setFiltros({
            certificadoId: "",
            productoId: "",
            valorMontoDescuento: "",
            codigoMes: "",
            qnProducto: "", 
            qnUtilizado: "" 
          });
          setPagina(1);
        }}
        showAdd={false}
        addLabel="Nuevo"
        onAddClick={() => setMostrarCrear(true)}
        pageSize={porPagina}
        onPageSizeChange={(size) => {
          setPorPagina(size);
          setPagina(1);
        }}
      />

      {/* Loading / Error */}
      {loading && <div className="mt-2 text-sm text-center text-gray-600">Cargando datos...</div>}
      {error && <div className="mt-2 text-sm text-red-600">{error}</div>}

      {/* DRAWER FILTROS */}
      {mostrarFiltros && (
        <FiltroProdCertificado
          initialFilters={filtros}
          onSubmit={(nuevosFiltros) => {
            setFiltros(nuevosFiltros);
            setPagina(1);
            setMostrarFiltros(false);
          }}
          onClose={() => setMostrarFiltros(false)}
        />
      )}

      {/* === TABLA DINÁMICA (REEMPLAZA TABLE) === */}
      {!loading && (
        <DynamicTable 
          size="M" // 6 Columnas -> Breakpoint 1024px
          data={data}
          columns={columns}
        />
      )}

      {/* FOOTER */}
      <ListFooter
        page={pagina}
        pageSize={porPagina}
        total={totalBackend}
        onPageChange={(p) => setPagina(p)}
      />

      {/* MODALES */}
      {mostrarCrear && (
        <CrearProdCertificado
          onClose={() => setMostrarCrear(false)}
          onCreated={() => {
            setReloadKey((k) => k + 1);
            setMostrarCrear(false);
          }}
        />
      )}

      {registroAEditar && (
        <EditarProdCertificado
          initial={registroAEditar}
          onClose={() => setRegistroAEditar(null)}
          onSaved={() => {
            setReloadKey((k) => k + 1);
            setRegistroAEditar(null);
          }}
        />
      )}

      {registroAEliminar && (
        <div className="modal-backdrop" onClick={() => setRegistroAEliminar(null)}>
          <div className="modal-panel-md" onClick={(e) => e.stopPropagation()}>
            <EliminarProdCertificado
              registro={registroAEliminar}
              onClose={() => setRegistroAEliminar(null)}
              onDeleted={() => {
                setReloadKey((k) => k + 1);
                setRegistroAEliminar(null);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}